/**
 * RestoID — corrections to Logto's shipped phrase bundles.
 * ---------------------------------------------------------------------------
 * WHY. The Russian half of `@logto/phrases-experience` reads like machine
 * translation in places, and in a few places it is simply ungrammatical
 * ("Чтобы продолжить, вы будете выйти из текущей учетной записи"). It also uses
 * three spellings of "account" at random and gets Russian plural categories
 * wrong. That text is the login page most RestoCapital users ever read, so it
 * has to be ours.
 *
 * HOW. Phrases arrive from the SERVER (`/api/.well-known/phrases`, see
 * `src/i18n/utils.ts`) — they are not bundled — so they cannot be patched at
 * build time. Instead this module registers an i18next listener at import time
 * and merges `OVERRIDES` over whatever the server just delivered, on first
 * initialization and again after every language switch.
 *
 * ORDERING. The listener is installed when this module is first imported, which
 * happens through `custom/components/RestoSettingsProvider` — i.e. while
 * `src/App.tsx`'s import graph is still being evaluated, long before any
 * react-i18next component mounts and subscribes. i18next calls listeners in
 * registration order, so the corrected strings are in the store before anything
 * re-renders with the raw ones.
 *
 * TRADE-OFF. This also wins over Logto Custom Phrases configured in the tenant,
 * which the architecture otherwise prefers (`custom/RESTOID-DESIGN-PLAN.md`).
 * That is deliberate for these keys: they are product copy we own and want under
 * review in git, not console state someone can silently change. A key that is
 * NOT listed here still falls through to Custom Phrases exactly as before.
 *
 * ADDING A STRING. Put the dotted key and the finished text in `OVERRIDES`. Keep
 * every interpolation placeholder (`{{name}}`, `{{count}}`, `<link></link>`)
 * byte-identical to the upstream phrase — i18next resolves them positionally by
 * name, and a dropped placeholder renders as empty text with no error.
 */
import i18next from 'i18next';

/** Flat dotted key → corrected text, per language. */
type Overrides = Record<string, Record<string, string>>;

export const OVERRIDES: Overrides = {
  ru: {
    /* --- Descriptions and screen copy --- */
    'description.agree_with_terms': 'Я соглашаюсь с ',
    'description.agree_with_terms_modal': 'Чтобы продолжить, согласитесь с <link></link>.',
    'description.all_account_creation_options': 'Все варианты создания аккаунта',
    'description.authorize_agreement': 'Разрешая доступ, вы соглашаетесь с <link></link> {{name}}.',
    'description.authorize_agreement_with_redirect':
      'Разрешая доступ, вы соглашаетесь с <link></link> {{name}} и будете перенаправлены на {{uri}}.',
    'description.authorize_organization_access': 'Разрешить доступ к организации:',
    'description.create_account_id_exists': 'Аккаунт {{value}} уже существует. Продолжить вход?',
    'description.create_account_id_exists_alert':
      'Указанный {{type}} {{value}} уже привязан к другому аккаунту. Попробуйте другой {{type}}.',
    'description.enter_passcode': 'Код подтверждения отправлен на {{address}} {{target}}',
    'description.enter_username_description':
      'Имя пользователя — альтернативный способ входа. Оно может содержать только буквы, цифры и знаки подчёркивания.',
    'description.enter_username_policy_description':
      'Имя пользователя — альтернативный способ входа. {{requirements}}',
    'description.identifier_register_description':
      'Введите {{types, list(type: disjunction;)}}, чтобы создать новый аккаунт.',
    'description.identifier_sign_in_description':
      'Введите {{types, list(type: disjunction;)}} для входа.',
    'description.link_account_id_exists':
      'Указанный {{type}} {{value}} уже зарегистрирован. Привязать существующий аккаунт?',
    'description.link_email_description':
      'Для дополнительной безопасности привяжите почту к аккаунту.',
    'description.link_email_or_phone_description':
      'Для дополнительной безопасности привяжите почту или номер телефона к аккаунту.',
    'description.link_phone_description':
      'Для дополнительной безопасности привяжите номер телефона к аккаунту.',
    'description.no_account': 'Ещё нет аккаунта? ',
    'description.no_region_code_found': 'Код региона не найден',
    'description.passcode_sent': 'Код отправлен повторно',
    'description.password_changed': 'Пароль изменён',
    'description.password_expired': 'Срок действия пароля истёк. Чтобы войти, сбросьте пароль.',
    'description.password_requirement.character_types_few':
      'минимум {{count}} типа символов — прописные и строчные буквы, цифры, спецсимволы',
    'description.password_requirement.character_types_many':
      'минимум {{count}} типов символов — прописные и строчные буквы, цифры, спецсимволы',
    'description.password_requirement.character_types_one':
      'минимум {{count}} тип символов — прописные и строчные буквы, цифры, спецсимволы',
    'description.password_requirement.character_types_other':
      'минимум {{count}} типа символов — прописные и строчные буквы, цифры, спецсимволы',
    'description.password_requirement.length_few': 'минимум {{count}} символа',
    'description.password_requirement.length_many': 'минимум {{count}} символов',
    'description.password_requirement.length_one': 'минимум {{count}} символ',
    'description.password_requirement.length_other': 'минимум {{count}} символа',
    'description.password_requirements': 'Требования к паролю: {{items, list}}.',
    'description.resend_after_seconds':
      'Код не пришёл? Отправить повторно через <span>{{seconds}}</span> с',
    'description.resend_passcode': 'Код не пришёл? <a>Отправить повторно</a>',
    'description.reset_password': 'Восстановление пароля',
    'description.reset_password_description':
      'Введите {{types, list(type: disjunction;)}} вашего аккаунта, и мы отправим код для восстановления пароля.',
    'description.reset_password_magic_link_description':
      'Введите адрес электронной почты, привязанный к вашему аккаунту, чтобы восстановить пароль.',
    'description.sign_in_id_does_not_exist': 'Аккаунт {{value}} не найден. Создать новый?',
    'description.sign_in_id_does_not_exist_alert':
      'Этот {{type}} ({{value}}) не связан ни с одним аккаунтом.',
    'description.sign_in_to_your_account': 'Войдите в аккаунт',
    'description.single_sign_on_connectors_list':
      'Ваша компания включила корпоративный вход (SSO) для {{email}}. Выберите провайдера, чтобы продолжить.',
    'description.single_sign_on_enabled': 'Для этого аккаунта включён корпоративный вход (SSO)',
    'description.social_bind_with_existing':
      'Мы нашли уже зарегистрированный аккаунт — его можно привязать сразу.',
    'description.social_identity_exist':
      'Этот {{type}} ({{value}}) уже связан с другим аккаунтом. Попробуйте другой.',
    'description.support_email': 'Почта поддержки: <link></link>',
    'description.switch_account_description':
      'Вы выйдете из текущего аккаунта и автоматически войдёте в новый.',
    'description.switch_account_title': 'Сейчас вы вошли как {{account}}',
    'description.unregistered_client_notice':
      'Название и логотип приложения заявлены самим доменом <hostname>{{host}}</hostname> и никем не проверены. Проверьте адрес, прежде чем продолжить.',
    'description.verification_method.passkey_description':
      'Подтвердите с помощью устройства или USB-ключа',

    /* --- Actions --- */
    'action.agree': 'Принимаю',
    'action.authorize': 'Разрешить',
    'action.create_account_without_linking': 'Создать новый аккаунт',
    'action.got_it': 'Понятно',
    'action.save_password': 'Сохранить пароль',
    'action.sign_in_via_passcode': 'Войти по коду подтверждения',
    'action.sign_in_via_passkey': 'Войти по ключу доступа',
    'action.sign_in_via_password': 'Войти по паролю',
    'action.single_sign_on': 'Корпоративный вход (SSO)',
    'action.use_another_account': 'Войти в другой аккаунт',
    'action.verify_via_passkey': 'Подтвердить ключом доступа',

    /* --- Input labels --- */
    'input.label_with_optional': '{{label}} (необязательно)',
    'input.search_region_code': 'Поиск кода страны',

    /* --- 2FA / MFA --- */
    'mfa.add_another_mfa_description': 'Выберите, чем ещё подтверждать личность при входе.',
    'mfa.add_another_mfa_factor': 'Добавить ещё один способ подтверждения',
    'mfa.add_authenticator_app': 'Добавить приложение-аутентификатор',
    'mfa.add_mfa_description':
      'Двухфакторная аутентификация включена. Выберите второй способ подтверждения для безопасного входа.',
    'mfa.backup_code_copied': 'Резервные коды скопированы.',
    'mfa.backup_code_hint': 'Обязательно скопируйте коды и сохраните их в надёжном месте.',
    'mfa.copy_and_paste_key_description':
      'Вставьте ключ в приложение-аутентификатор — например, Google Authenticator, Duo Mobile или Authy.',
    'mfa.create_a_passkey': 'Создать ключ доступа',
    'mfa.create_passkey_description':
      'Используйте биометрию устройства, ключ безопасности (например, YubiKey) или другой доступный способ.',
    'mfa.email_verification_code': 'Код на почту',
    'mfa.enter_email_verification_code': 'Введите код из письма',
    'mfa.enter_email_verification_code_description':
      'Для этого аккаунта включена двухфакторная аутентификация. Введите код, отправленный на {{identifier}}.',
    'mfa.enter_one_time_code_description':
      'Для этого аккаунта включена двухфакторная аутентификация. Введите одноразовый код из приложения-аутентификатора.',
    'mfa.enter_one_time_code_link_description':
      'Введите 6-значный код из приложения-аутентификатора.',
    'mfa.enter_phone_verification_code': 'Введите код из СМС',
    'mfa.enter_phone_verification_code_description':
      'Для этого аккаунта включена двухфакторная аутентификация. Введите код из СМС, отправленный на {{identifier}}.',
    'mfa.link_another_mfa_factor': 'Переключиться на другой способ',
    'mfa.link_backup_code_description': 'Создайте резервные коды',
    'mfa.link_email_2fa_description': 'Понадобится для двухфакторной аутентификации.',
    'mfa.link_email_verification_code_description': 'Привяжите адрес электронной почты',
    'mfa.link_phone_2fa_description': 'Понадобится для двухфакторной аутентификации.',
    'mfa.link_phone_verification_code_description': 'Привяжите номер телефона',
    'mfa.link_webauthn_description': 'Привяжите устройство или ключ безопасности',
    'mfa.onboarding_description':
      'Защитите аккаунт двухфакторной аутентификацией. Выберите один или несколько способов: ключ доступа, приложение-аутентификатор, код из СМС или резервные коды.',
    'mfa.phone_verification_code': 'Код из СМС',
    'mfa.qr_code_not_available': 'Не удаётся отсканировать QR-код?',
    'mfa.save_backup_code': 'Сохраните резервные коды',
    'mfa.save_backup_code_description':
      'Используйте один из этих кодов, если не сможете подтвердить вход другим способом. Каждый код действует один раз.',
    'mfa.scan_qr_code_description':
      'Подойдёт любое приложение-аутентификатор — например, Google Authenticator, Duo Mobile или Authy.',
    'mfa.totp': 'Приложение-аутентификатор',
    'mfa.try_another_verification_method': 'Попробовать другой способ подтверждения',
    'mfa.verify_email_verification_code_description': 'Введите код, отправленный на почту',
    'mfa.verify_mfa_description':
      'Для этого аккаунта включена двухфакторная аутентификация. Выберите второй способ подтверждения личности.',
    'mfa.verify_phone_verification_code_description': 'Введите код, отправленный на телефон',
    'mfa.verify_totp_description': 'Введите одноразовый код из приложения',
    'mfa.verify_via_passkey': 'Подтвердите ключом доступа',
    'mfa.verify_via_passkey_description':
      'Подойдёт пароль устройства или биометрия, сканирование QR-кода либо ключ безопасности (например, YubiKey).',
    'mfa.verify_webauthn_description': 'Подтвердите устройством или ключом безопасности',
    'mfa.webauthn': 'Ключ доступа',
    'mfa.webauthn_failed_to_create': 'Не удалось создать ключ доступа. Попробуйте ещё раз.',
    'mfa.webauthn_failed_to_verify': 'Не удалось подтвердить ключом доступа. Попробуйте ещё раз.',
    'mfa.webauthn_not_supported': 'Этот браузер не поддерживает ключи доступа.',

    /* --- Errors --- */
    'error.access_denied': 'Доступ запрещён',
    'error.account_suspended_description': 'Обратитесь к администратору за помощью.',
    'error.invalid_connector_request': 'Запрос на вход недействителен.',
    'error.invalid_link_description':
      'Возможно, срок действия ссылки истёк или она больше не действительна.',
    'error.invalid_session': 'Сессия не найдена. Войдите снова.',
    'error.password_rejected.character_types': 'Не менее {{min}} типов символов.',
    'error.password_rejected.pwned': 'Не используйте простые пароли, которые легко угадать.',
    'error.password_rejected.restricted.user_info': 'личные данные',
    'error.password_rejected.restricted.words': 'название сервиса',
    'error.password_rejected.restricted_found': 'Не используйте {{list, list}}.',
    'error.password_rejected.too_long': 'Не более {{max}} символов.',
    'error.password_rejected.too_short': 'Не менее {{min}} символов.',
    'error.passwords_do_not_match': 'Пароли не совпадают.',
    'error.sso_not_enabled': 'Для этой почты не включён корпоративный вход (SSO).',
    'error.terms_acceptance_required': 'Условия не приняты',
    'error.terms_acceptance_required_description': 'Чтобы продолжить, примите условия.',
    'error.username_invalid_charset':
      'Имя пользователя может содержать только буквы, цифры и подчёркивания.',
    'error.username_lowercase_not_allowed': 'Имя пользователя не может содержать строчные буквы.',
    'error.username_numbers_not_allowed': 'Имя пользователя не может содержать цифры.',
    'error.username_should_not_start_with_number': 'Имя пользователя не должно начинаться с цифры.',
    'error.username_underscore_not_allowed': 'Имя пользователя не может содержать подчёркивания.',
    'error.username_uppercase_not_allowed': 'Имя пользователя не может содержать заглавные буквы.',

    /* --- Passkey sign-in --- */
    'passkey_sign_in.setup_page.description':
      'Создайте ключ доступа с помощью биометрии устройства, ключа безопасности вроде YubiKey или другого доступного способа.',
    'passkey_sign_in.setup_page.subtitle': 'Создайте ключ доступа',
    'passkey_sign_in.trigger_conditional_ui_failed':
      'Не удалось запустить автозаполнение ключа доступа.',

    /* --- Consent scopes --- */
    'user_scopes.descriptions.custom_data': 'Ваши дополнительные данные',
    'user_scopes.descriptions.identities': 'Ваши привязанные социальные аккаунты',
    'user_scopes.descriptions.urn:logto:scope:organization_roles': 'Ваши роли в организациях',
  },
  en: {},
};

/** I18next stores phrases nested, so `a.b.c` has to become `{ a: { b: { c } } }`. */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const expand = (flat: Record<string, string>): Record<string, unknown> => {
  const root: Record<string, unknown> = {};

  for (const [dotted, value] of Object.entries(flat)) {
    const path = dotted.split('.');
    const leaf = path.pop();

    if (!leaf) {
      continue;
    }

    let node: Record<string, unknown> | undefined = root;

    for (const segment of path) {
      const next: unknown = node[segment];

      if (next === undefined) {
        const created: Record<string, unknown> = {};
        node[segment] = created;
        node = created;
      } else if (isRecord(next)) {
        node = next;
      } else {
        // A leaf already sits here — two overrides collide. Drop this one rather
        // than replace a string with an object and break every sibling lookup.
        node = undefined;
        break;
      }
    }

    if (node) {
      node[leaf] = value;
    }
  }

  return root;
};

const applyFor = (language: string): void => {
  // 'ru-RU' and 'ru' both take the 'ru' table; anything else falls back to 'en'.
  const table = language.toLowerCase().startsWith('ru') ? OVERRIDES.ru : OVERRIDES.en;

  if (!table || Object.keys(table).length === 0) {
    return;
  }

  // Deep merge into the server bundle; overwrite so ours wins.
  i18next.addResourceBundle(language, 'translation', expand(table), true, true);
};

let installed = false;

/** Registers the listeners once. Safe to call repeatedly. */
export const installRestoIdPhrases = (): void => {
  if (installed) {
    return;
  }
  installed = true;

  i18next.on('initialized', () => {
    applyFor(i18next.language);
  });
  i18next.on('languageChanged', (language: string) => {
    applyFor(language);
  });

  // Covers the case where i18next finished initializing before this ran.
  if (i18next.isInitialized) {
    applyFor(i18next.language);
  }
};

installRestoIdPhrases();
